import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, input, Renderer2, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Menu, MenuModule } from 'primeng/menu';
import { filter, tap } from 'rxjs';
import { ProfileModalService } from '../../../../../shared/services/profileModal.service';
import { MenuTool } from '../../interfaces/MenuTool.interface';
import { NavigationItem } from '../../interfaces/navigationList.interface';
import {
  HomeButtonConfig,
  Profile,
} from '../../interfaces/PageContainerConfiguration.interface';
import { PageContainerService } from '../../services/page-container.service';
import { AppsToolsMenuComponent } from '../apps-tools-menu/apps-tools-menu.component';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { SharedService } from '../../../../../shared/services/shared.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthenticationService } from '../../../../auth/services/authentication.service';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'st-side-menu-bar',
  standalone: true,
  imports: [
    CommonModule,
    IconButtonComponent,
    AppsToolsMenuComponent,
    MenuModule,
    RouterModule,
    TooltipModule
  ],
  templateUrl: './side-menu-bar.component.html',
  styleUrls: ['./side-menu-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SideMenuBarComponent {
  navigationList = input.required<NavigationItem[]>();
  homeButton = input.required<HomeButtonConfig>();
  tools = input.required<MenuTool[]>();
  profile = input.required<Profile>();
  settingURL = input<string>();
  public isAboutModalVisible = signal<boolean>(false);
  public userName = signal<string>('');
  public userEmail = signal<string>('');
  dropDownItems: MenuItem[] = [];
  constructor(
    readonly pageContainerService: PageContainerService,
    readonly router: Router,
    private readonly _renderer: Renderer2,
    private readonly profileModalService: ProfileModalService,
    private readonly _sharedService: SharedService,
    private readonly _router: Router,
    public authenticationService: AuthenticationService,
  ) {
    this.setUserDetailsFromLocalStorage();
  }

  private readonly destroyRef = inject(DestroyRef);

  private setUserDetailsFromLocalStorage(): void {
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      const user = JSON.parse(currentUser);
      this.userName.set(user?.user_profile?.full_name ?? 'Unknown User');
      this.userEmail.set(user?.user_profile?.email ?? 'Unknown Email');

      this.dropDownItems = [
        {
          label: this.userName(),
          items: [
            {
              label: 'My Profile',
              icon: 'icon-user-circle',
              shortcut: '⌘+E',
              disabled: false,
              command: () => this.profileModalService.openProfileModal(),
            },
            {
              label: 'About Adroit',
              icon: 'icon-info',
              shortcut: '⌘+a',
              disabled: false,
              command: () => this.profileModalService.openAboutModal(),
            },
            {
              label: 'Sign Out',
              icon: 'icon-sign-out',
              shortcut: '⌘+d',
              disabled: false,
              command: () => this.signOut(),
            },
          ],
        },
      ];
    }
  }

  isHomeButtonActive = signal(false);

  routerEvents$ = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd), // type guard
    tap(event => {
      if (event.urlAfterRedirects === this.homeButton().routeUrl) {
        this.isHomeButtonActive.set(true);
      } else {
        this.isHomeButtonActive.set(false);
      }
    }),
  );

  isExpandedMenu = signal<boolean>(false);

  ngAfterViewInit() {
    if (this.router.url === this.homeButton().routeUrl) {
      this.isHomeButtonActive.set(true);
    } else {
      this.isHomeButtonActive.set(false);
    }
  }

  expandMenu() {
    this.isExpandedMenu.update(x => !x);
    this.pageContainerService.menuExpanded.next(this.isExpandedMenu());
  }

  goToHome(url: string) {
    this.pageContainerService.activeSectionRoute.next(null);
  }

  public toggleMenu(event: Event, menu: Menu) {
    menu.toggle(event);

    this._renderer.setStyle(
      menu.el.nativeElement,
      'top',
      `${(event as any).y}px !important`,
    );
    this._renderer.setStyle(
      menu.el.nativeElement,
      'left',
      `${(event as any).x}px !important`,
    );
  }

  public signOut() {
    this._sharedService.logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: res => {},
      error: (error: HttpErrorResponse) => {},
      complete: () => {
        this.pageContainerService.activeSectionRoute.next(null);
        this.authenticationService.removeUserData();
        this._router.navigate(['/login']);
      },
    });
  }
}
